<script lang="ts">
  import Layout from '../../../widgets/Layout.svelte';
  import { getSubscriptionState, type ChannelDetail } from '../../../firebase/api/channel';
  import defaultHeader from '../../../assets/default-banner.png';
  import defaultIcon from '../../../assets/default-icon.png';
  import type { Plan, PlanSubscription } from '../../../firebase/common/plan';
  import { MEANINGLESS_CHANNEL_ID } from '../../../firebase/common/user';
  import { onMount } from 'svelte';
  import TabBar from '@smui/tab-bar';
  import Tab, { Icon, Label } from '@smui/tab';
  import Paper, { Content } from '@smui/paper';
  import Ripple from '@smui/ripple';
  import PlanCard from './PlanCard.svelte';
  import FeedAbstractList from '../../../widgets/FeedAbstractList.svelte';
  import type { FeedAbstract } from '../../../firebase/common/feed';
  import CircularProgress from '@smui/circular-progress';
  import type { TransitionConfig } from 'svelte/transition';
  import { cubicInOut } from 'svelte/easing';
  import EditPlan from './EditPlan.svelte';
  import { checkoutPlan } from '../../../utils/checkout';
  import Button from '../../../widgets/Button.svelte';
  import twIcon from '../../../assets/twitter-icon.png';
  import webIcon from '../../../assets/website.png';

  interface FeedResult {
    count: number;
    feeds: FeedAbstract[];
  }

  interface PageData {
    channel: ChannelDetail;
    tab: string;
    page: number;
    streamed: {
      plans: Promise<Plan[] | { errored: true }>;
      feeds: Promise<FeedResult | { errored: true }>;
    };
  }

  interface InOutConfig {
    duration: number;
  }

  export let data: PageData;
  let currentCID: string | null = null;
  let retrieveSubscription: Promise<PlanSubscription | null>;

  const loadingInOut = (node: Element, { duration }: InOutConfig): TransitionConfig => {
    return {
      duration,
      css(t) {
        const ease = cubicInOut(1 - t);
        return `transform: scale(${ease});`;
      },
    };
  };

  const tabs = [
    {
      icon: 'dynamic_feed',
      label: '投稿',
      key: 'feeds',
    },
    {
      icon: 'bookmarks',
      label: 'プラン',
      key: 'plans',
    },
  ];

  let active: (typeof tabs)[number];
  $: active = tabs.find((it) => it.key === data.tab) ?? tabs[0]!;

  onMount(() => {
    if (currentCID !== MEANINGLESS_CHANNEL_ID && currentCID !== null) {
      retrieveSubscription = getSubscriptionState(data.channel.id, currentCID);
    }
  });

  $: {
    if (currentCID !== MEANINGLESS_CHANNEL_ID && currentCID !== null) {
      retrieveSubscription = getSubscriptionState(data.channel.id, currentCID);
    }
  }

  const createEmptyPlan = () => {
    return {
      created_at: Date.now(),
      description: '',
      name: '',
      price: 500,
      thumbnail: '',
    } as Plan;
  };

  let editPlan: boolean = false;
  let newPlan: boolean = false;
  let editingPlan: Plan = createEmptyPlan();
</script>

<svelte:head>
  <title>{data.channel.name} - AIFAN</title>
  <meta charset="utf-8" />
  <meta name="description" content={data.channel.bio} />
  <meta name="viewport" content="width=device-width,minimum-scale=1,initial-scale=1" />
  <meta name="format-detection" content="telephone=no, email=no, address=no" />
  <meta property="og:site_name" content="AIFAN" />
  <meta property="og:url" content="https://aifan.ai/c/{data.channel.display_name}" />
  <meta property="og:type" content="website" />
  <meta property="og:title" content="{data.channel.name} - AIFAN" />
  <meta property="og:description" content={data.channel.bio} />
  <meta property="og:image" content={data.channel.icon ?? defaultIcon} />
  <meta property="og:locale" content="ja_JP" />
  <meta name="twitter:card" content="summary" />
  <meta name="twitter:site" content="@{data.channel.twitter_url ?? 'AIFAN_ai'}" />
  <meta name="twitter:title" content="{data.channel.name} - AIFAN" />
  <meta name="twitter:description" content={data.channel.bio} />
  <meta name="twitter:image:src" content={data.channel.icon ?? defaultIcon} />
  <link rel="canonical" href="https://aifan.ai/c/{data.channel.display_name}" />
</svelte:head>

<Layout bind:currentCID>
  <div class="card">
    <div class="channel-heading">
      <div class="header-image" style="background-image: url({data.channel.header ?? defaultHeader})">
        <div class="icon">
          <img src={data.channel.icon ?? defaultIcon} alt="{data.channel.name}のアイコン" />
        </div>
      </div>
      <div class="biography">
        <div class="name">
          <h2>{data.channel.name}</h2>
          {#if data.channel.twitter_url}
            <a href="http://twitter.com/{data.channel.twitter_url}">
              <img src={twIcon} alt="twitterプロフィール" />
            </a>
          {/if}
          {#if data.channel.web_url}
            <a href={data.channel.web_url}>
              <img src={webIcon} alt="webページ" />
            </a>
          {/if}
        </div>
        <div class="bio">{data.channel.bio}</div>
      </div>
    </div>
    <article>
      <TabBar let:tab {tabs} bind:active class="tab">
        <Tab {tab} href="/c/{data.channel.display_name}?tab={tab.key}">
          <Icon class="material-symbols-outlined">{tab.icon}</Icon>
          <Label>{tab.label}</Label>
        </Tab>
      </TabBar>
      {#if active.key === 'feeds'}
        <Paper variant="unelevated" style="position: relative" class="sp-paper">
          <Content>
            {#await Promise.all([data.streamed.feeds, data.streamed.plans, retrieveSubscription])}
              <div class="loading" transition:loadingInOut={{ duration: 400 }}>
                <div class="loading-sizer">
                  <CircularProgress indeterminate style="width: 4rem; height: 4rem" />
                </div>
              </div>
            {:then [feeds, plans, subscription]}
              {#if 'errored' in feeds || 'errored' in plans}
                <div class="no-feeds">
                  <p>投稿の取得に失敗しました</p>
                </div>
              {:else if feeds.count === 0}
                <div class="no-feeds">
                  <p>まだ投稿はありません</p>
                </div>
              {:else}
                {@const plan = plans.filter((it) => it.price === subscription?.price)?.[0] ?? null}
                <FeedAbstractList
                  owner={currentCID === data.channel.id}
                  channel={data.channel}
                  feeds={feeds.feeds}
                  {plans}
                  {currentCID}
                  currentPlan={plan}
                />
                {#if feeds.count > 50}
                  {@const last = Math.ceil(feeds.count / 50)}
                  <div class="pagination-wrapper">
                    <div class="pagination">
                      {#if data.page > 2}
                        <div class="first nth-button" use:Ripple={{ surface: true, color: 'primary' }}>
                          <a href="?p=1">{'<<'}</a>
                        </div>
                      {/if}
                      {#each [...new Array(7).keys()]
                        .map((it) => it + data.page - 2)
                        .filter((it) => it > 0 && it <= last) as page}
                        <div class="nth-button" use:Ripple={{ surface: true, color: 'primary' }}>
                          <a href="?p={page}">{page}</a>
                        </div>
                      {/each}
                      {#if data.page < last - 1}
                        <div class="last nth-button" use:Ripple={{ surface: true, color: 'primary' }}>
                          <a href="?p={last}">{'>>'}</a>
                        </div>
                      {/if}
                    </div>
                  </div>
                {/if}
              {/if}
            {/await}
          </Content>
        </Paper>
      {:else if active.key === 'plans'}
        <Paper variant="unelevated" style="position: relative" class="sp-paper">
          <Content>
            {#await Promise.all([data.streamed.plans, retrieveSubscription])}
              <div class="loading" transition:loadingInOut={{ duration: 400 }}>
                <div class="loading-sizer">
                  <CircularProgress indeterminate style="width: 4rem; height: 4rem" />
                </div>
              </div>
            {:then [plans, subscription]}
              {#if 'errored' in plans}
                <div class="no-feeds">
                  <p>プランの取得に失敗しました</p>
                </div>
              {:else}
                {@const plan = plans.filter((it) => it.price === subscription?.price)?.[0] ?? null}
                {#if plan}
                  <PlanCard
                    {plan}
                    currentPlan
                    owner={currentCID === data.channel.id}
                    onPlanEdit={() => {
                      editingPlan = plan;
                      editPlan = true;
                    }}
                  />
                {/if}
                {#each plans as plan}
                  {#if plan.price !== subscription?.price}
                    <PlanCard
                      {plan}
                      owner={currentCID === data.channel.id}
                      onPlanEdit={() => {
                        editingPlan = plan;
                        newPlan = false;
                        editPlan = true;
                      }}
                      onPlanSubscribe={checkoutPlan(currentCID, data.channel.id, plan.price)}
                    />
                  {/if}
                {/each}
                {#if currentCID === data.channel.id}
                  <div class="add-plan">
                    <Button
                      type="button"
                      onclick={() => {
                        editingPlan = createEmptyPlan();
                        newPlan = true;
                        editPlan = true;
                      }}>プランを作成</Button
                    >
                  </div>
                {/if}
              {/if}
            {/await}
          </Content>
        </Paper>
      {/if}
    </article>
  </div>
  {#await data.streamed.plans then plans}
    {#if !('errored' in plans)}
      <EditPlan bind:editingPlan bind:editPlan bind:newPlan {plans} channel_id={currentCID ?? ''} />
    {/if}
  {/await}
</Layout>

<style lang="scss">
  $sp-width: 456px;
  $tb-width: 880px;
  .card {
    --body-width: 50rem;
    border-radius: 1rem;
    min-height: 100%;
    width: var(--body-width);
    margin: auto;
    overflow: hidden;
    background-color: #fff;
  }

  .pagination {
    width: fit-content;
    margin: auto;
    border-radius: 4px;
    padding: 0.5rem 1rem;
    display: flex;
    column-gap: 0.5rem;

    .nth-button {
      padding: 5px;
      border-radius: 2rem;
      min-width: 2.5ex;
      text-align: center;
    }
  }

  .loading {
    justify-content: center;
    margin: 3rem;
    transition: 200ms;
    transform: translateY(0) scale(1);
    position: absolute;
    left: 0;
    right: 0;
    top: 3rem;

    .loading-sizer {
      width: min-content;
      margin: auto;
    }
  }

  .no-feeds {
    display: flex;
    font-size: 1rem;
    font-weight: 400;
    margin: 1rem 0;
    color: var(--text-gray);

    p {
      margin: auto;
    }
  }

  .add-plan {
    margin: 2rem;
  }

  article {
    margin-top: 2rem;
  }

  .channel-heading {
    .header-image {
      width: 100%;
      height: auto;
      aspect-ratio: 3/1;
      background-size: cover;
      position: relative;

      .icon {
        position: absolute;
        width: 8rem;
        height: 8rem;
        border-radius: 9in;
        overflow: hidden;
        border: solid 0.2rem white;
        bottom: -5rem;
        left: 2rem;

        > img {
          width: 100%;
          height: 100%;
        }
      }
    }
    .biography {
      padding: 0.75rem 1rem 0 12rem;

      .name {
        display: flex;
        column-gap: 0.5rem;

        h2 {
          margin: 0 0 0.25rem;
          font-size: 1.4rem;
        }

        a {
          display: flex;

          img {
            width: 1.6rem;
            height: 1.6rem;
            margin: auto;
          }
        }
      }

      .bio {
        font-size: 0.9rem;
        line-height: 1.65;
        color: var(--text-gray);
      }
    }
  }
  :global(.tab) {
    background-color: #fff;
  }
  @media screen and (max-width: #{$tb-width}) {
    .card {
      display: flex;
      flex-direction: column;
      border-radius: 0;
      width: 100%;
    }

    article {
      margin-top: 1rem;
      height: 100%;
      flex-grow: 1;
      background-color: var(--bg-gray);
    }

    .channel-heading {
      .header-image {
        .icon {
          width: 6rem;
          height: 6rem;
          bottom: -1rem;
          left: 50%;
          border: solid 0.1rem white;
          transform: translateX(-50%);
        }
      }
      .biography {
        padding: 1.5rem 1rem 0;

        .name h2 {
          font-size: 1.2rem;
        }
      }
    }
    :global(.sp-paper) {
      height: 100%;
      background-color: var(--bg-gray) !important; // 申し訳ない、、、これ以外思いつきませんでした
    }
  }

  :global(.mdc-dialog) {
    z-index: 10000;
  }
</style>
